import React, { useState } from "react";
import { Drawer, Button, Modal, notification, Space } from "antd";
import { useTranslation } from "react-i18next";
import documentService from "../services/documentService";
import StatusBadge from "views/components/common/statusbadge/StatusBadge";
import { DocumentPreviewPane } from "./DocumentPreviewDrawer";

/**
 * Read-only failure drawer (roast R06). Failed invoices used to be dead
 * ends — an error toast on edit and no way forward. The drawer shows what
 * was received, why it failed, and offers Archive / Reprocess so corpses
 * stop piling up in the queue.
 */

const FAILED_STATUSES = [
  "invalid_file_format",
  "invalid_pet_invoice",
  "extraction_issue",
];

export const isFailedStatus = (status) => FAILED_STATUSES.includes(status);

const FailureDrawer = ({ record, open, onClose, onActionDone }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  if (!record) return null;

  const refreshAndClose = () => {
    onActionDone && onActionDone();
    onClose();
  };

  const runAction = async (actionFn, successKey, errorKey) => {
    setBusy(true);
    try {
      await actionFn(record.id);
      notification.success({ message: t(successKey) });
      refreshAndClose();
    } catch (err) {
      notification.error({
        message: t(errorKey),
        description: t("genericError"),
      });
    } finally {
      setBusy(false);
    }
  };

  const confirmArchive = () => {
    Modal.confirm({
      title: t("archiveConfirmTitle"),
      content: t("archiveConfirmContent"),
      okText: t("archive"),
      cancelText: t("cancel"),
      onOk: () =>
        runAction(documentService.archive, "documentArchived", "archiveError"),
    });
  };

  const confirmReprocess = () => {
    Modal.confirm({
      title: t("reprocessConfirmTitle"),
      content: t("reprocessConfirmContent"),
      okText: t("reprocess"),
      cancelText: t("cancel"),
      onOk: () =>
        runAction(
          documentService.reprocess,
          "documentReprocessing",
          "reprocessError"
        ),
    });
  };

  const metaRow = (labelKey, value) => (
    <>
      <span className="meta-label">{t(labelKey)}</span>
      <span className="meta-value">{value || t("notAvailable")}</span>
    </>
  );

  return (
    <Drawer
      title={record.file_name}
      placement="right"
      width={480}
      open={open}
      onClose={onClose}
      className="failure-drawer"
      destroyOnClose
    >
      <div className="failure-reason-banner">
        <div className="mb-1">
          <StatusBadge extractionStatus={record.extraction_status} />
        </div>
        {t(`failure_${record.extraction_status}`)}
      </div>

      <div className="failure-meta">
        {metaRow("status", t("not_extracted"))}
        {metaRow("receivedFrom", record.received_from)}
        {metaRow("receivedAt", record.received_at)}
        {metaRow("noOfPages", record.number_of_pages)}
      </div>

      <div className="failure-preview">
        {record.id ? (
          <div style={{ width: "100%", height: "60vh" }}>
            <DocumentPreviewPane
              documentId={record.id}
              url={record.sas_document_link}
              fileName={record.file_name}
              active={open}
            />
          </div>
        ) : (
          <span className="text-gray-j">{t("notAvailable")}</span>
        )}
      </div>

      <Space className="mt-3" style={{ width: "100%", justifyContent: "flex-end" }}>
        <Button onClick={confirmArchive} loading={busy} danger>
          {t("archive")}
        </Button>
        <Button type="primary" onClick={confirmReprocess} loading={busy}>
          {t("reprocess")}
        </Button>
      </Space>
    </Drawer>
  );
};

export default FailureDrawer;
